import React from 'react'
import { Flex } from '@jisr-hr/design-system'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, useFormState } from 'react-final-form'
import { useRequest } from 'components/global/templates/RequestDrawer/RequestProvider'
import {
  CorrectionDateAndType,
  ProposedCorrectionTime,
  Reason,
  Profile,
} from 'components/global/templates/RequestDrawer/components/forms'
import { getEmployeeAttendanceDetails } from 'redux/attendance/employeeDetails/actionsCreators'
import { missingPunchRequestCreate } from 'redux/actions/missingPunchRequestActions'
import { addDays } from 'date-fns'
import { get24hour } from 'components/global/form/formNormalize'
import JisrLoader from 'components/global/JisrLoader'
import { attendanceLogsLoad } from 'redux/attendance/timesheets/requestDetails/actionCreators'

const handleDate = (val, isNext) => {
  const date = isNext ? addDays(new Date(val?.correction_date), 1) : new Date(val?.correction_date)

  return {
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear(),
  }
}

const handleTime = (val, type) => {
  const punch = type === 'in' ? val?.in_punch : val?.out_punch
  const { punch_time } = punch
  const date = type === 'in' ? handleDate(val) : handleDate(val, punch?.isOutNext)
  const time = punch_time && get24hour(punch_time)

  const dateUTC = new Date(Date.UTC(date.year, date.month, date.day, time.hour, time.minutes))
  return dateUTC.toISOString()
}

const CorrectionRequest = () => {
  const dispatch = useDispatch()
  const {
    values: { correction_date, attendance_record_id, shift },
  } = useFormState()
  const { change } = useForm()
  const { loading } = useSelector(({ requestDetails }) => requestDetails)
  const { fetching: createFetching } = useSelector(({ missingPunchRequest }) => missingPunchRequest)
  const { attendance_record, requestFetching: fetching } = useSelector(
    ({ employeeAttendanceDetails }) => employeeAttendanceDetails,
  )

  const record = attendance_record?.[0]
  const selectedShift = record?.employee_shifts?.find(({ id }) => id === shift)

  const { setOnSubmit, setInitialValues, vars, drawerToggle, actionCallback } = useRequest()

  const handleShiftList = () => {
    dispatch(
      getEmployeeAttendanceDetails(
        vars.id,
        {
          dates: [correction_date],
          include_attendance_log_details: true,
        },
        true,
      ),
    )
  }

  const handleOnSubmit = (values) => {
    const val = {
      employee_shift_id: values?.shift,
      attendance_record_id: values?.attendance_record_id,
      justification: values.reason,
      attachments: values.attachments,
      ...(values?.in_punch && {
        in_time: handleTime(values, 'in'),
        ...(values?.in_punch?.id && { in_time_log_id: values?.in_punch?.id }),
      }),
      ...(values?.out_punch && {
        out_time: handleTime(values, 'out'),
        ...(values?.out_punch?.id && { out_time_log_id: values?.out_punch?.id }),
      }),
    }

    return dispatch(missingPunchRequestCreate(vars?.id, val)).then(() => {
      drawerToggle()
      if (actionCallback) {
        actionCallback()
      }
    })
  }

  React.useEffect(() => {
    setOnSubmit(handleOnSubmit)
    setInitialValues({
      punches: {},
      correction_date: vars?.date,
      attendance_record_id: vars?.recordId,
    })

    return () => {
      setOnSubmit(() => {})
      setInitialValues({})
    }
  }, [])

  React.useEffect(() => {
    if (correction_date) {
      change('attendance_record_id', undefined)
      change('shift', undefined)
      handleShiftList()
    }
  }, [correction_date])

  React.useEffect(() => {
    if (attendance_record_id) {
      dispatch(attendanceLogsLoad(attendance_record_id))
    }
  }, [attendance_record_id])

  return (
    <Flex
      flexCol
      style={{ gap: '16px' }}
    >
      {(fetching || createFetching || loading) && <JisrLoader absolute />}
      <Profile employee={vars?.employee} />
      <CorrectionDateAndType />
      <ProposedCorrectionTime
        employee_shifts={attendance_record?.[0]?.employee_shifts}
        shift={{
          is_shift_got_ended: _.isEmpty(record?.employee_shifts),
          in_time_log: {
            reporting_method: record?.in_time_log?.reporting_method,
            reporting_method_i18n: record?.in_time_log?.reporting_method_i18n,
          },
          out_time_log: {
            reporting_method: record?.out_time_log?.reporting_method,
            reporting_method_i18n: record?.out_time_log?.reporting_method_i18n,
          },
          in_time: record?.in_time,
          out_time: record?.out_time,
          ...selectedShift,
        }}
      />
      <Reason />
    </Flex>
  )
}

export default CorrectionRequest
